/**
 * ReachProCompareModal — Outil de comparaison ReachPro ↔ YAS
 *
 * 🔒 COMPOSANT 100% ISOLÉ — Pour le retirer du projet :
 *   1. Supprimer ce fichier
 *   2. Retirer l'import et le bouton "Comparer ReachPro" dans AideListingView.jsx
 *   → Aucune autre dépendance, aucun side-effect global, aucune route backend.
 *
 * L'utilisateur colle un HTML brut copié depuis l'inventaire ReachPro.
 * Le composant :
 *   1. Parse le HTML (DOMParser + heuristiques sur cellules <td>) pour extraire
 *      { matchName, block, rang, qty, active }
 *   2. Construit la "vérité interne" depuis les tickets ACHAT non-attribués passés en prop
 *   3. Compare en groupant par (match normalisé, block, rang)
 *      — Les billets sans emplacement (pas de block/rang) sont comparés
 *        uniquement par quantité totale par match.
 *   4. Affiche le résultat avec 3 statuts :
 *        ✅ Match exact            (présent des deux côtés, qty identique)
 *        🟡 Manquant dans ReachPro (présent en interne, absent/insuffisant chez ReachPro)
 *        🔴 En trop dans ReachPro  (présent chez ReachPro, absent/excédent en interne)
 */
import React, { useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, ScanSearch, AlertTriangle, CheckCircle2, Eraser, Upload, FileText } from 'lucide-react';
import { cn } from '../../lib/utils';

// ───────────────────────── HELPERS ─────────────────────────

// Réutilise la même extraction que AideListingView pour avoir des noms comparables
const extractTeamsOnly = (eventName) => {
  if (!eventName) return '';
  let s = String(eventName).trim();
  s = s.replace(/^\s*M\d+\b[\s\-:.]*/i, '');
  s = s.replace(/\(\s*(groupe|group)\s+[A-Z]\s*\)\s*[-–]?\s*/i, '');
  s = s.replace(/\(\s*match\s+\d+\s*\)\s*$/i, '').trim();
  s = s.replace(/\s*[-–·.]\s*(World\s*Cup|WC)\b.*$/i, '').trim();
  s = s.replace(/^[\s\-–·]+|[\s\-–·]+$/g, '').trim();
  return s;
};

// Normalise un libellé match pour comparaison robuste (accents, casse, séparateurs, "vs"…)
const normMatch = (s) => {
  if (!s) return '';
  return String(s)
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')         // retire accents
    .replace(/\bvs?\.?\b|\s[-–·]\s|,|\s+v\s+/g, ' vs ')        // unifie séparateurs
    .replace(/\bmexique\b/g, 'mexico')
    .replace(/\bafrique du sud\b/g, 'south africa')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normCell = (s) => String(s ?? '').replace(/\s+/g, ' ').trim();

const numOrNull = (s) => {
  if (s === null || s === undefined) return null;
  const m = String(s).match(/-?\d+/);
  return m ? parseInt(m[0], 10) : null;
};

// ───────────────────────── PARSER ─────────────────────────

/**
 * Parse l'HTML ReachPro en entrées { matchName, block, rang, qty, active, raw }.
 * Stratégie tolérante :
 *   - DOMParser → on cible toutes les <tr> de tous les <table>
 *   - Si une <tr> est un en-tête (contient th), on retient les noms de colonnes
 *   - Pour les autres <tr>, on map les <td> sur les colonnes
 *   - On détecte le statut actif via classe/texte (active, inactive, hidden, off, paused, suspendu)
 *   - On déduit le match depuis : colonne "Match"/"Event"/"Évènement", sinon depuis le
 *     plus proche <h1|h2|h3|h4|caption> précédant la table.
 */
function parseReachProHTML(rawHtml) {
  const html = String(rawHtml || '').trim();
  if (!html) return { items: [], warnings: ['HTML vide'] };

  const warnings = [];
  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div id="__root__">${html}</div>`, 'text/html');
  } catch (e) {
    return { items: [], warnings: ['HTML invalide : ' + e.message] };
  }

  const items = [];
  const tables = Array.from(doc.querySelectorAll('table'));

  // Heuristique de mapping des colonnes
  const COL_KEYS = {
    match:  /\b(match|event|evenement|évènement|évènement|évenement)\b/i,
    block:  /\b(block|bloc|section|sect)\b/i,
    row:    /\b(row|rang|rangée|rangee|rg)\b/i,
    qty:    /\b(qty|qte|qté|quantite|quantité|total|tickets?|nb)\b/i,
    status: /\b(status|statut|state|active|actif|listing)\b/i,
    price:  /\b(price|prix|cost)\b/i,
    date:   /\b(date|day|jour)\b/i,
  };

  const detectCol = (header) => {
    const txt = normCell(header).toLowerCase();
    for (const [key, re] of Object.entries(COL_KEYS)) if (re.test(txt)) return key;
    return null;
  };

  // Trouve l'en-tête le plus proche avant une table (h1-h4, caption)
  const findContextMatch = (table) => {
    const cap = table.querySelector(':scope > caption');
    if (cap && cap.textContent.trim()) return cap.textContent.trim();
    let n = table.previousElementSibling;
    let depth = 0;
    while (n && depth < 20) {
      if (/^H[1-6]$/i.test(n.tagName) && n.textContent.trim()) return n.textContent.trim();
      n = n.previousElementSibling;
      depth++;
    }
    // remonter au parent et regarder ses h1-h4
    let p = table.parentElement;
    let pd = 0;
    while (p && pd < 4) {
      const h = p.querySelector('h1, h2, h3, h4');
      if (h && h.textContent.trim()) return h.textContent.trim();
      p = p.parentElement;
      pd++;
    }
    return '';
  };

  // Détecte actif/inactif
  const detectActive = (tr, statusText) => {
    const cls = (tr.className || '').toLowerCase();
    const txt = (statusText || tr.textContent || '').toLowerCase();
    if (/\b(inactive|hidden|off|paused|suspendu|disabled|expired|sold)\b/.test(txt)) return false;
    if (/\b(inactive|disabled|hidden|paused|off)\b/.test(cls)) return false;
    if (/\b(active|actif|live|en\s*ligne|listed|on)\b/.test(txt)) return true;
    if (/\b(active|live|listed)\b/.test(cls)) return true;
    return true; // par défaut, on suppose actif
  };

  if (tables.length === 0) {
    warnings.push('Aucun tableau <table> détecté. Essayez de copier la table complète depuis ReachPro.');
  }

  for (const table of tables) {
    const contextMatch = findContextMatch(table);
    const rows = Array.from(table.querySelectorAll('tr'));
    if (rows.length === 0) continue;

    // Chercher la ligne d'en-tête (avec <th>) — sinon, premier <tr>
    let headerIdx = rows.findIndex(r => r.querySelector('th'));
    if (headerIdx === -1) headerIdx = 0;
    const headerCells = Array.from(rows[headerIdx].querySelectorAll('th, td')).map(c => normCell(c.textContent));
    const colMap = headerCells.map(detectCol);

    // Détecte si la première ligne est bien un header (au moins 2 colonnes reconnues)
    const recognized = colMap.filter(Boolean).length;
    const hasHeader = recognized >= 2;
    const startIdx = hasHeader ? headerIdx + 1 : 0;

    for (let i = startIdx; i < rows.length; i++) {
      const tr = rows[i];
      const cells = Array.from(tr.querySelectorAll('td, th')).map(c => normCell(c.textContent));
      if (cells.length === 0) continue;
      // Ignore re-headers
      if (tr.querySelector('th') && cells.some(c => /match|block|row|qty|total/i.test(c))) continue;

      const row = { matchName: contextMatch, block: '', rang: '', qty: null, active: true, raw: cells.join(' | ') };
      let statusText = '';

      if (hasHeader) {
        cells.forEach((c, idx) => {
          const key = colMap[idx];
          if (!key) return;
          if (key === 'match' && c) row.matchName = c;
          else if (key === 'block') row.block = c;
          else if (key === 'row') row.rang = c;
          else if (key === 'qty') row.qty = numOrNull(c);
          else if (key === 'status') statusText = c;
        });
      } else {
        // Heuristique : essayer de deviner par contenu de cellule
        for (const c of cells) {
          if (!row.block && /\bblock|bloc/i.test(c)) row.block = c.replace(/.*?:\s*/, '').trim();
          else if (!row.rang && /\brow|rang/i.test(c)) row.rang = c.replace(/.*?:\s*/, '').trim();
          else if (row.qty === null && /\bqty|total|qté|quantité/i.test(c)) row.qty = numOrNull(c);
          else if (!statusText && /(active|inactive|listed|paused)/i.test(c)) statusText = c;
        }
        // Si toujours rien et la cellule est purement numérique → suppose une qté
        if (row.qty === null) {
          const numCells = cells.filter(c => /^\d+$/.test(c));
          if (numCells.length === 1) row.qty = parseInt(numCells[0], 10);
        }
      }

      // Nettoyage : extraire les chiffres bruts pour block/rang
      if (row.block) row.block = String(row.block).replace(/^[a-z\s:]*/i, '').trim();
      if (row.rang) row.rang = String(row.rang).replace(/^[a-z\s:]*/i, '').trim();

      row.active = detectActive(tr, statusText);

      // On garde la ligne si elle a au moins une info exploitable (qty ou block+rang)
      if (row.qty !== null || (row.block || row.rang)) {
        items.push(row);
      }
    }
  }

  return { items, warnings };
}

// ───────────────────────── JSON PARSER (extension YAS) ─────────────────────────

/**
 * Parse le JSON exporté par l'extension Chrome "YAS - Export ReachPro".
 * Format attendu :
 * {
 *   source: 'reachpro-yas-extension',
 *   event: '...',
 *   rows: [{ block, block_full, rang, total, qty, active, ... }]
 * }
 */
function parseReachProJSON(content) {
  const warnings = [];
  let data;
  try {
    data = JSON.parse(content);
  } catch (e) {
    return { items: [], warnings: ['JSON invalide : ' + e.message] };
  }

  if (!data || !Array.isArray(data.rows)) {
    return { items: [], warnings: ['Format JSON inattendu : champ "rows" manquant'] };
  }
  if (data.source !== 'reachpro-yas-extension') {
    warnings.push('Source inconnue, parsing tenté quand même');
  }

  const eventName = data.event || 'Sans match';
  const items = data.rows.map((r) => ({
    matchName: eventName,
    block: r.block != null ? String(r.block).trim() : '',
    rang: r.rang != null ? String(r.rang).trim() : '',
    qty: r.qty != null ? Number(r.qty) : (r.total != null ? Number(r.total) : null),
    active: r.active !== false, // true par défaut
    raw: r.block_full || `${r.block || ''} ${r.rang || ''}`.trim(),
  })).filter((it) => it.qty !== null || it.block || it.rang);

  return { items, warnings };
}

// ───────────────────────── INTERNAL BUILDER ─────────────────────────

/**
 * Construit la "vérité interne" depuis les tickets ACHAT NON-attribués (à lister).
 * Renvoie par match (normalisé) :
 *   { matchLabel, placements: Map<"block|rang", qty>, withoutPlacementQty }
 */
function buildInternalByMatch(tickets) {
  const byMatch = new Map();
  for (const t of tickets) {
    if (t.type !== 'ACHAT') continue;
    if (t.attribue) continue; // on ne compare que les non-attribués (= ce que l'on doit lister)
    const label = t.evenement || 'Sans match';
    const teams = extractTeamsOnly(label) || label;
    const key = normMatch(teams);
    if (!byMatch.has(key)) {
      byMatch.set(key, {
        matchLabel: teams,
        rawLabel: label,
        placements: new Map(),
        withoutPlacementQty: 0,
      });
    }
    const entry = byMatch.get(key);
    const hasPlace = (t.block || t.rang);
    if (hasPlace) {
      const pk = `${(t.block || '').trim()}|${(t.rang || '').trim()}`;
      entry.placements.set(pk, (entry.placements.get(pk) || 0) + 1);
    } else {
      entry.withoutPlacementQty += 1;
    }
  }
  return byMatch;
}

/**
 * Construit la vue ReachPro groupée par match (normalisé) :
 *   { matchLabel, placements: Map<"block|rang", qty>, withoutPlacementQty }
 * On agrège les quantités si plusieurs lignes ReachPro partagent la même clé.
 * Les lignes INACTIVES sont ignorées de la comparaison (mais signalées).
 */
function buildReachProByMatch(items) {
  const byMatch = new Map();
  let inactiveCount = 0;
  for (const it of items) {
    if (!it.active) { inactiveCount++; continue; }
    const label = it.matchName || 'Sans match';
    const teams = extractTeamsOnly(label) || label;
    const key = normMatch(teams);
    if (!byMatch.has(key)) {
      byMatch.set(key, {
        matchLabel: teams,
        rawLabel: label,
        placements: new Map(),
        withoutPlacementQty: 0,
      });
    }
    const entry = byMatch.get(key);
    const qty = it.qty && it.qty > 0 ? it.qty : 1;
    const hasPlace = (it.block || it.rang);
    if (hasPlace) {
      const pk = `${(it.block || '').trim()}|${(it.rang || '').trim()}`;
      entry.placements.set(pk, (entry.placements.get(pk) || 0) + qty);
    } else {
      entry.withoutPlacementQty += qty;
    }
  }
  return { byMatch, inactiveCount };
}

// ───────────────────────── COMPARE ─────────────────────────

/**
 * Compare interne vs ReachPro et renvoie un tableau de lignes :
 *   { matchKey, matchLabel, block, rang, internalQty, reachQty, status }
 *   status: 'match' | 'missing_in_reach' | 'extra_in_reach'
 */
function compare(internalByMatch, reachByMatch) {
  const rows = [];
  const allKeys = new Set([...internalByMatch.keys(), ...reachByMatch.keys()]);

  for (const key of allKeys) {
    const intM = internalByMatch.get(key);
    const reachM = reachByMatch.get(key);
    const label = intM?.matchLabel || reachM?.matchLabel || key;
    const allPlaceKeys = new Set([
      ...(intM?.placements?.keys() || []),
      ...(reachM?.placements?.keys() || []),
    ]);
    for (const pk of allPlaceKeys) {
      const [block, rang] = pk.split('|');
      const iq = intM?.placements?.get(pk) || 0;
      const rq = reachM?.placements?.get(pk) || 0;
      let status;
      if (iq === rq) status = 'match';
      else if (iq > rq) status = 'missing_in_reach';
      else status = 'extra_in_reach';
      rows.push({
        matchKey: key, matchLabel: label,
        block, rang,
        internalQty: iq, reachQty: rq, status,
        isNoPlace: false,
      });
    }
    // Sans emplacement → quantité globale
    const iNP = intM?.withoutPlacementQty || 0;
    const rNP = reachM?.withoutPlacementQty || 0;
    if (iNP > 0 || rNP > 0) {
      let status;
      if (iNP === rNP) status = 'match';
      else if (iNP > rNP) status = 'missing_in_reach';
      else status = 'extra_in_reach';
      rows.push({
        matchKey: key, matchLabel: label,
        block: '—', rang: '—',
        internalQty: iNP, reachQty: rNP, status,
        isNoPlace: true,
      });
    }
  }

  // Tri : par match puis par status (missing/extra avant match), puis par block/rang
  const statusOrder = { missing_in_reach: 0, extra_in_reach: 1, match: 2 };
  rows.sort((a, b) => {
    if (a.matchLabel !== b.matchLabel) return a.matchLabel.localeCompare(b.matchLabel);
    if (statusOrder[a.status] !== statusOrder[b.status]) return statusOrder[a.status] - statusOrder[b.status];
    if (a.isNoPlace !== b.isNoPlace) return a.isNoPlace ? 1 : -1;
    const ba = parseInt(a.block, 10) || 0;
    const bb = parseInt(b.block, 10) || 0;
    if (ba !== bb) return ba - bb;
    const ra = parseInt(a.rang, 10) || 0;
    const rb = parseInt(b.rang, 10) || 0;
    return ra - rb;
  });

  return rows;
}

// ───────────────────────── COMPONENT ─────────────────────────

const STATUS_META = {
  match:            { icon: '✅', label: 'Match',             cls: 'bg-emerald-950/60 border-emerald-700/40 text-emerald-200' },
  missing_in_reach: { icon: '🟡', label: 'Manquant ReachPro', cls: 'bg-yellow-950/60 border-yellow-700/40 text-yellow-200' },
  extra_in_reach:   { icon: '🔴', label: 'En trop ReachPro',  cls: 'bg-red-950/60 border-red-700/40 text-red-200' },
};

const ReachProCompareModal = ({ open, onOpenChange, tickets = [] }) => {
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [parsed, setParsed] = useState(null); // { items, warnings }
  const [isParsing, setIsParsing] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [readError, setReadError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'match' | 'missing_in_reach' | 'extra_in_reach'
  const fileInputRef = useRef(null);

  const runParse = (content, isJson) => {
    setIsParsing(true);
    // setTimeout 30 pour permettre au spinner de s'afficher avant le parse synchrone
    setTimeout(() => {
      const result = isJson ? parseReachProJSON(content) : parseReachProHTML(content);
      setParsed(result);
      setIsParsing(false);
    }, 30);
  };

  const handleFile = (file) => {
    if (!file) return;
    // Accepte .html, .htm, .txt, .json, ou tout fichier text/*
    const name = file.name || '';
    const okExt = /\.(html?|htm|txt|json)$/i.test(name);
    const okType = (file.type || '').startsWith('text/')
      || file.type === ''
      || file.type === 'application/xhtml+xml'
      || file.type === 'application/json';
    if (!okExt && !okType) {
      setReadError(`Format non supporté : "${name}". Utilise un fichier .html, .htm, .json ou .txt`);
      return;
    }
    const isJson = /\.json$/i.test(name) || file.type === 'application/json';
    setReadError('');
    setFileName(name);
    setFileSize(file.size || 0);
    setIsReading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = String(e.target.result || '');
      setIsReading(false);
      runParse(content, isJson);
    };
    reader.onerror = () => {
      setIsReading(false);
      setReadError('Impossible de lire le fichier.');
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleReset = () => {
    setFileName('');
    setFileSize(0);
    setParsed(null);
    setReadError('');
    setFilter('all');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatBytes = (n) => {
    if (!n) return '';
    if (n < 1024) return `${n} o`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
    return `${(n / (1024 * 1024)).toFixed(2)} Mo`;
  };

  const internalByMatch = useMemo(() => buildInternalByMatch(tickets), [tickets]);
  const reach = useMemo(() => parsed ? buildReachProByMatch(parsed.items) : null, [parsed]);
  const rows = useMemo(() => reach ? compare(internalByMatch, reach.byMatch) : [], [internalByMatch, reach]);

  const counts = useMemo(() => {
    const c = { match: 0, missing_in_reach: 0, extra_in_reach: 0 };
    for (const r of rows) c[r.status]++;
    return c;
  }, [rows]);

  const filteredRows = filter === 'all' ? rows : rows.filter(r => r.status === filter);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl w-[95vw] max-h-[92vh] overflow-hidden flex flex-col bg-zinc-900 border-zinc-700 text-zinc-100 p-0"
        data-testid="reachpro-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-800">
          <DialogTitle className="text-xl font-bold text-[#C4A052] flex items-center gap-2">
            <ScanSearch className="h-5 w-5" />
            Comparer ReachPro ↔ YAS
          </DialogTitle>
          <p className="text-xs text-zinc-400 mt-1">
            Importe le fichier <strong>.json</strong> exporté par l&apos;extension Chrome <em className="text-[#C4A052] not-italic">YAS - Export ReachPro</em>
            {' '}(recommandé), ou un fichier <strong>.html</strong> de page ReachPro. Lignes <span className="text-yellow-300">inactives</span> ignorées.
            Les billets <span className="text-zinc-300">sans emplacement</span> sont comparés par quantité par match.
          </p>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          {/* File upload zone */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-zinc-300 uppercase tracking-wide">
              Fichier HTML ReachPro
            </label>

            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const f = e.dataTransfer?.files?.[0];
                if (f) handleFile(f);
              }}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'cursor-pointer rounded-lg border-2 border-dashed px-6 py-8 text-center transition-all',
                isDragging
                  ? 'border-[#C4A052] bg-[#C4A052]/10'
                  : 'border-zinc-700 hover:border-[#C4A052]/60 bg-zinc-950/40 hover:bg-zinc-900/60'
              )}
              data-testid="reachpro-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.html,.htm,.txt,application/json,text/html,text/plain"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="reachpro-file-input"
              />
              {isReading ? (
                <div className="flex flex-col items-center gap-2 text-zinc-300">
                  <Loader2 className="h-8 w-8 animate-spin text-[#C4A052]" />
                  <span className="text-sm">Lecture du fichier...</span>
                </div>
              ) : fileName ? (
                <div className="flex flex-col items-center gap-1.5">
                  <FileText className="h-8 w-8 text-emerald-400" />
                  <div className="text-sm font-semibold text-emerald-200" data-testid="reachpro-filename">{fileName}</div>
                  <div className="text-xs text-zinc-500">{formatBytes(fileSize)} · Cliquez pour changer de fichier</div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-zinc-300">
                  <Upload className="h-8 w-8 text-[#C4A052]/80" />
                  <div className="text-sm font-semibold">Glisse-dépose un fichier <span className="text-[#C4A052]">.json</span> ou <span className="text-[#C4A052]">.html</span> ici, ou clique pour parcourir</div>
                  <div className="text-xs text-zinc-500">Formats acceptés : .json (extension YAS) · .html · .htm · .txt</div>
                </div>
              )}
            </div>

            {readError && (
              <div className="text-xs text-red-300 flex items-start gap-1.5 bg-red-950/40 border border-red-800/40 rounded p-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div>{readError}</div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isReading || isParsing}
                className="bg-[#C4A052] hover:bg-[#D4B86A] text-black font-bold"
                data-testid="reachpro-pick-file-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                {fileName ? 'Changer de fichier' : 'Choisir un fichier'}
              </Button>
              <Button
                onClick={handleReset}
                variant="ghost"
                className="text-zinc-400 hover:text-white"
                data-testid="reachpro-reset-btn"
              >
                <Eraser className="h-4 w-4 mr-2" /> Effacer
              </Button>
              {(isParsing || isReading) && (
                <span className="text-xs text-[#C4A052] flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {isReading ? 'Lecture...' : 'Analyse en cours...'}
                </span>
              )}
              {parsed && !isParsing && (
                <span className="text-xs text-zinc-500 ml-auto">
                  {parsed.items.length} ligne{parsed.items.length > 1 ? 's' : ''} extraite{parsed.items.length > 1 ? 's' : ''} depuis ReachPro
                  {reach && reach.inactiveCount > 0 && (
                    <span className="text-yellow-400 ml-2">({reach.inactiveCount} inactive{reach.inactiveCount > 1 ? 's' : ''} ignorée{reach.inactiveCount > 1 ? 's' : ''})</span>
                  )}
                </span>
              )}
            </div>
            {parsed && parsed.warnings.length > 0 && (
              <div className="text-xs text-yellow-400 flex items-start gap-1.5 bg-yellow-950/30 border border-yellow-800/40 rounded p-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div>{parsed.warnings.join(' · ')}</div>
              </div>
            )}
          </div>

          {/* Results */}
          {parsed && (
            <>
              {/* Summary + filters */}
              <div className="flex items-center gap-2 flex-wrap bg-zinc-800/50 rounded-lg px-3 py-2">
                <span className="text-sm text-zinc-300 mr-2">Résultats :</span>
                {[
                  { id: 'all', label: 'TOUS', count: rows.length, color: 'bg-zinc-700 hover:bg-zinc-600 text-zinc-200 border-zinc-600' },
                  { id: 'match', label: '✅ Match', count: counts.match, color: 'bg-emerald-700 hover:bg-emerald-600 text-white border-emerald-500' },
                  { id: 'missing_in_reach', label: '🟡 Manquant ReachPro', count: counts.missing_in_reach, color: 'bg-yellow-700 hover:bg-yellow-600 text-white border-yellow-500' },
                  { id: 'extra_in_reach', label: '🔴 En trop ReachPro', count: counts.extra_in_reach, color: 'bg-red-700 hover:bg-red-600 text-white border-red-500' },
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={cn(
                      'px-3 py-1 text-xs font-bold rounded border transition-all',
                      f.color,
                      filter === f.id ? 'ring-2 ring-[#C4A052] ring-offset-1 ring-offset-zinc-900' : 'opacity-70 hover:opacity-100'
                    )}
                    data-testid={`reachpro-filter-${f.id}`}
                  >
                    {f.label} <span className="ml-1 opacity-80">({f.count})</span>
                  </button>
                ))}
              </div>

              {/* Result table */}
              {filteredRows.length === 0 ? (
                <div className="text-center py-10 text-zinc-500">
                  <CheckCircle2 className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>Aucune ligne pour ce filtre</p>
                </div>
              ) : (
                <div className="border border-zinc-700 rounded-lg overflow-hidden">
                  <table className="w-full text-sm" data-testid="reachpro-result-table">
                    <thead className="bg-zinc-800 text-zinc-300">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Statut</th>
                        <th className="text-left px-3 py-2 font-semibold">Match</th>
                        <th className="text-left px-3 py-2 font-semibold">Block</th>
                        <th className="text-left px-3 py-2 font-semibold">Rang</th>
                        <th className="text-right px-3 py-2 font-semibold">Qté YAS</th>
                        <th className="text-right px-3 py-2 font-semibold">Qté ReachPro</th>
                        <th className="text-right px-3 py-2 font-semibold">Δ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {filteredRows.map((r, idx) => {
                        const meta = STATUS_META[r.status];
                        const delta = r.internalQty - r.reachQty;
                        return (
                          <tr key={idx} className={cn('hover:bg-zinc-800/50', meta.cls)} data-testid={`reachpro-row-${idx}`}>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="font-bold">{meta.icon}</span> <span className="text-xs">{meta.label}</span>
                            </td>
                            <td className="px-3 py-1.5 font-medium">{r.matchLabel}</td>
                            <td className="px-3 py-1.5 font-mono">{r.isNoPlace ? <em className="text-zinc-400 not-italic">Sans empl.</em> : (r.block || '—')}</td>
                            <td className="px-3 py-1.5 font-mono">{r.isNoPlace ? '—' : (r.rang || '—')}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{r.internalQty}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{r.reachQty}</td>
                            <td className={cn('px-3 py-1.5 text-right font-mono font-bold', delta > 0 ? 'text-yellow-300' : delta < 0 ? 'text-red-300' : 'text-emerald-300')}>
                              {delta > 0 ? `+${delta}` : delta}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="text-[11px] text-zinc-500 leading-relaxed pt-1">
                <strong>Lecture :</strong> <span className="text-emerald-300">Match</span> = qté identique des deux côtés. {' '}
                <span className="text-yellow-300">Manquant ReachPro</span> = présent chez YAS mais absent ou en moins chez ReachPro
                (à lister !). {' '}
                <span className="text-red-300">En trop ReachPro</span> = ReachPro affiche plus que YAS (revendu / déjà attribué / à retirer).
              </div>
            </>
          )}

          {!parsed && !isReading && !isParsing && (
            <div className="text-center py-12 text-zinc-500 border border-dashed border-zinc-700 rounded-lg">
              <ScanSearch className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Importe ton fichier <strong>.json</strong> (extension YAS) ou <strong>.html</strong> ReachPro ci-dessus.</p>
              <p className="text-xs mt-1 opacity-70">
                <strong className="text-[#C4A052]">Recommandé :</strong> installe l&apos;extension Chrome <em>YAS - Export ReachPro</em> et clique sur le bouton &quot;Exporter vers YAS&quot; sur la page ReachPro.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReachProCompareModal;
